// Modules
import React, { useEffect, useMemo, useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'

// Components
import { usePhasedLogin } from 'redesign/hooks/usePhasedLogin'
import {
    CPFPhasedLogin,
    CustomBrandEmphasisLogin,
    OnboardingNewUser,
    MobileServiceLogin,
    CustomNewLogin,
    CustomSinlgeBrand,
} from 'redesign/components/organisms'
import { useRedirectExternalUniqueToken } from 'hooks'
import { ROUTES_PATH, SESSION_STORAGES_KEYS, STORAGE_CURRENT_LABORATORY } from 'constants/index'
import { useCurrentLaboratory } from 'redesign/hooks/useCurrentLaboratory'

// Application
import { useCustomLayoutStore } from 'redesign/store'
import { FullPageLoading } from 'components'
import { SignUpModalError } from 'components/Presenters/SelectContact/SignUpModalError'
import { clearStorage, getQueryParamsValue } from 'utils'
import { UNICO_CONSTANTS } from 'redesign/constants/unico'

export const PhasedLoginStepper = () => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''
    const { getStep, steps, phasedLoginVisibility, loading, checkKeepLoggedData } = usePhasedLogin()
    const { verifyQueryParamsAndSaveSessionStorage } = useRedirectExternalUniqueToken()
    const { currentLayout, currentLaboratory, layoutType } = useCurrentLaboratory()
    const { layoutConfig, activeLoginPage } = useCustomLayoutStore()
    const [showModalError, setShowModalError] = useState(false)
    const location = useLocation()
    const productIds = getQueryParamsValue(location, 'productIds')

    useEffect(() => {
        checkKeepLoggedData()
        verifyQueryParamsAndSaveSessionStorage()
        sessionStorage.removeItem('@LiviaRecoverPassword:Username')
        sessionStorage.removeItem('@LiviaAuth:SignUpPersonalData')
        sessionStorage.removeItem('@Livia/federalTaxId')
        sessionStorage.removeItem('@LiviaAuth:UnicoBlocked')
        const shouldShowModal = sessionStorage.getItem('@LiviaAuth:ShowSignUpModalError') === 'true'

        if (shouldShowModal) {
            setShowModalError(true)
            sessionStorage.removeItem('@LiviaAuth:ShowSignUpModalError')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const numberOfItems = productIds?.split(',').length ?? 0
        currentLaboratory?.layout
            ? sessionStorage.setItem(STORAGE_CURRENT_LABORATORY, JSON.stringify({ layoutType, numberOfItems, ...currentLaboratory }))
            : sessionStorage.removeItem(STORAGE_CURRENT_LABORATORY)
    }, [currentLaboratory, layoutType, productIds])

    useEffect(() => {
        clearStorage([UNICO_CONSTANTS.STORAGE, SESSION_STORAGES_KEYS.CONTACTS_BIOMETRY, SESSION_STORAGES_KEYS.REDIRECT_TO_URL])
    }, [])

    const showCustomLayout = !!layoutConfig

    const defaultLayout = useMemo(() => activeLoginPage?.default, [activeLoginPage])
    const mobileServiceLayout = useMemo(() => activeLoginPage?.mobileService, [activeLoginPage])
    const currentLayoutDefault = useMemo(() => !showCustomLayout && currentLayout.default, [showCustomLayout, currentLayout])
    const currentLayoutNew = useMemo(() => !showCustomLayout && currentLayout.brandEmphasis, [showCustomLayout, currentLayout])
    const currentLayoutNewLogin = useMemo(() => !showCustomLayout && currentLayout.newLogin, [showCustomLayout, currentLayout])
    const currentLayoutSingleBrand = useMemo(() => !showCustomLayout && currentLayout.singleBrand, [showCustomLayout, currentLayout])

    function CurrentLayout() {
        if (defaultLayout) {
            return <CPFPhasedLogin />
        }
        if (mobileServiceLayout) {
            return <MobileServiceLogin />
        }
        if (currentLayoutDefault) {
            return <CPFPhasedLogin />
        }
        if (currentLayoutNew) {
            return <CustomBrandEmphasisLogin />
        }
        if (currentLayoutNewLogin) {
            return <CustomNewLogin />
        }
        if (currentLayoutSingleBrand) {
            return <CustomSinlgeBrand />
        }

        return <CPFPhasedLogin />
    }

    if (!phasedLoginVisibility) {
        return <Redirect to={ROUTES_PATH.LOGIN} />
    }

    if (loading) {
        return <FullPageLoading />
    }

    switch (getStep()) {
        case steps.PHASED_LOGIN_ONBOARDING_NEW_USER:
            return <OnboardingNewUser />
        default:
            return (
                <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey} useEnterprise>
                    <CurrentLayout />
                    {showModalError && <SignUpModalError setShowModal={setShowModalError} showModal={showModalError} />}
                </GoogleReCaptchaProvider>
            )
    }
}
